import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ReactElement } from 'react';
import { SessionPersistenceType } from '@pillar-portal/api-types';
import type {
    CreateProcessorRequest,
    GetProcessorResponse,
    ProcessorConfig,
    SessionPersistenceType as SessionPersistenceTypeValue,
} from '@pillar-portal/api-types';
export type ProcessorEditorValue = {
    processorName: string;
    reportingDashboardName: string;
    reportingDashboardUrl: string;
    reportingDashboardAllowedUrlPatternsText: string;
    reportingDashboardSessionPersistence: SessionPersistenceTypeValue;
};

export function createEmptyProcessorEditorValue(): ProcessorEditorValue {
    return {
        processorName: '',
        reportingDashboardName: '',
        reportingDashboardUrl: '',
        reportingDashboardAllowedUrlPatternsText: '',
        reportingDashboardSessionPersistence: 'persistent',
    };
}

export function createProcessorEditorValueFromResponse(
    processor: GetProcessorResponse
): ProcessorEditorValue {
    const reportingDashboard = processor.processor_config.reporting_dashboard;
    return {
        processorName: processor.processor_name,
        reportingDashboardName: reportingDashboard?.name ?? '',
        reportingDashboardUrl: reportingDashboard?.url ?? '',
        reportingDashboardAllowedUrlPatternsText:
            reportingDashboard?.allowedUrlPatterns.join('\n') ?? '',
        reportingDashboardSessionPersistence:
            reportingDashboard?.sessionPersistence ?? 'persistent',
    };
}

function hasProcessorReportingDashboardInput(value: ProcessorEditorValue): boolean {
    return (
        value.reportingDashboardName.trim().length > 0 ||
        value.reportingDashboardUrl.trim().length > 0 ||
        value.reportingDashboardAllowedUrlPatternsText.trim().length > 0
    );
}

export function validateProcessorEditorValue(value: ProcessorEditorValue): string | null {
    if (value.processorName.trim().length === 0) {
        return 'Processor name is required.';
    }
    if (!hasProcessorReportingDashboardInput(value)) {
        return null;
    }
    if (value.reportingDashboardName.trim().length === 0) {
        return 'Reporting dashboard name is required.';
    }
    if (value.reportingDashboardUrl.trim().length === 0) {
        return 'Reporting dashboard URL is required.';
    }
    if (parseAllowedUrlPatternsText(value.reportingDashboardAllowedUrlPatternsText).length === 0) {
        return 'At least one reporting dashboard URL pattern is required.';
    }
    return null;
}

function parseAllowedUrlPatternsText(value: string): string[] {
    return value
        .split(/[\n,]/)
        .map((entry: string) => entry.trim())
        .filter((entry: string) => entry.length > 0);
}

export function buildProcessorCreateRequest(
    value: ProcessorEditorValue
): CreateProcessorRequest {
    return {
        processor_name: value.processorName.trim(),
        processor_config: buildProcessorConfig(value),
    };
}

export function buildProcessorConfig(value: ProcessorEditorValue): ProcessorConfig {
    if (!hasProcessorReportingDashboardInput(value)) {
        return {};
    }
    return {
        reporting_dashboard: {
            type: 'ipg',
            name: value.reportingDashboardName.trim(),
            url: value.reportingDashboardUrl.trim(),
            allowedUrlPatterns: parseAllowedUrlPatternsText(
                value.reportingDashboardAllowedUrlPatternsText
            ),
            sessionPersistence: value.reportingDashboardSessionPersistence,
        },
    };
}

export function parseSessionPersistenceType(
    value: string
): SessionPersistenceTypeValue {
    switch (value) {
        case SessionPersistenceType.PERSISTENT:
            return SessionPersistenceType.PERSISTENT;
        case SessionPersistenceType.EPHEMERAL:
            return SessionPersistenceType.EPHEMERAL;
        default:
            throw new Error(`Invalid session persistence type: ${value}`);
    }
}

export function ProcessorEditor(props: {
    value: ProcessorEditorValue;
    onChange: (value: ProcessorEditorValue) => void;
    disabled?: boolean;
}): ReactElement {
    const disabled: boolean = props.disabled ?? false;

    return (
        <div className="space-y-4">
            <div>
                <Label htmlFor="processor_name">Processor Name</Label>
                <Input
                    id="processor_name"
                    value={props.value.processorName}
                    onChange={(event) =>
                        props.onChange({
                            ...props.value,
                            processorName: event.target.value,
                        })
                    }
                    placeholder="Processor name"
                    disabled={disabled}
                />
            </div>
            <div className="space-y-4 rounded-md border p-4">
                <div className="font-medium">Reporting Dashboard</div>
                <div>
                    <Label htmlFor="reporting_dashboard_name">Name</Label>
                    <Input
                        id="reporting_dashboard_name"
                        value={props.value.reportingDashboardName}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardName: event.target.value,
                            })
                        }
                        placeholder="Reporting dashboard name"
                        disabled={disabled}
                    />
                </div>
                <div>
                    <Label htmlFor="reporting_dashboard_url">URL</Label>
                    <Input
                        id="reporting_dashboard_url"
                        value={props.value.reportingDashboardUrl}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardUrl: event.target.value,
                            })
                        }
                        placeholder="https://example.com/dashboard"
                        disabled={disabled}
                    />
                </div>
                <div>
                    <Label htmlFor="reporting_dashboard_allowed_url_patterns">
                        Allowed URL Patterns
                    </Label>
                    <Input
                        id="reporting_dashboard_allowed_url_patterns"
                        value={props.value.reportingDashboardAllowedUrlPatternsText}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardAllowedUrlPatternsText: event.target.value,
                            })
                        }
                        placeholder="https://example.com/*"
                        disabled={disabled}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                        Separate multiple URL patterns with commas or new lines.
                    </p>
                </div>
                <div>
                    <Label htmlFor="reporting_dashboard_session_persistence">
                        Session Persistence
                    </Label>
                    <select
                        id="reporting_dashboard_session_persistence"
                        value={props.value.reportingDashboardSessionPersistence}
                        onChange={(event) =>
                            props.onChange({
                                ...props.value,
                                reportingDashboardSessionPersistence:
                                    parseSessionPersistenceType(event.target.value),
                            })
                        }
                        disabled={disabled}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    >
                        <option value="persistent">Persistent</option>
                        <option value="ephemeral">Ephemeral</option>
                    </select>
                </div>
            </div>
        </div>
    );
}
